/**
 * Spec №22 (D-CL1/2/3/5) — the task's execution thread, mounted inside the task
 * detail (David's ruling: the thread lives where the record lives). The thread IS
 * the execution record:
 *   - ▶ Play runs the agent on the task under its OWN scope (venue = native), bound
 *     to this thread; the human interjects/assists by playing again with a message.
 *   - action lines render the verbs the agent called; the completion line counts
 *     tokens + model (D-CL7).
 *   - a Needs-You escalation (raised mid-run) renders inline; the human replies to
 *     assist (the agent continues) or takes over (the agent yields) — the two-button
 *     design David approved.
 *   - "Launch in Claude / ChatGPT" carries a task reference to a host; the work
 *     returns to this same thread under the host's venue chip.
 *
 * Restyle of the shipped AgentThread bubbles; token-only, theme-aware.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useTaskThread, usePlayTaskRun, useIsRunPlaying, useTakeOverTask, useTaskLaunchLinks, useAnswerNeedsYou, useSkipNeedsYou } from "../../hooks/use-task-thread";
import { DecisionCard } from "../collab/DecisionCard";
import { ChatMarkdown } from "../chat/ChatMarkdown";
import { useCoPanelOptional } from "../collab/co-panel-context";
import { useStickToBottom } from "../../hooks/use-stick-to-bottom";
import type { Task, TaskThreadMessage, TraceView, NamedSource } from "../../../lib/types";

/** №22.3 (D-CH4) — a continuation line focuses the panel on the next run's thread
 * instead of navigating (no full reload — D-CH3). `/app/task/:id` → the task id. */
function taskIdFromThreadHref(href: string | undefined): string | null {
  const m = (href ?? "").match(/\/app\/task\/([^/?#]+)/);
  return m ? m[1]! : null;
}

/**
 * PROPOSED (not yet the default) — what the docked panel puts ABOVE THE FOLD when
 * you CLICK a work item, as opposed to when you ANSWER one.
 *
 *   "full"       today. The whole task thread: every prior run's brief, transcript,
 *                receipts and resolved cards, with the live thing at the bottom.
 *                Correct as a RECORD. Wrong as a landing place — the panel shows a
 *                document and puts a composer under it, so the document reads as a
 *                chat that has lost its thread.
 *   "card-first" the thing you clicked, first: the live card (or, for a task with no
 *                card, its brief and ▶), then a one-line disclosure back to the full
 *                run. Nothing is hidden; the record is one click and stays the record.
 *
 * This is NOT a new pattern. №22.3.2 F2 already ruled that "rollup projections … are
 * never conversation turns" and that a card enters a conversation by being CLICKED;
 * `focusTask` has been opening the rollup instead. See docs/22.6-panel-entry-options.md.
 */
export type ThreadLens = "full" | "card-first";

interface Props {
  taskId: string;
  agentId: string;
  agentName: string;
  task: Task;
  /** №22.3.1 (D-AF5) — a hairline transition row at the top of the thread ("— now
   *  in: {task} · with {actor} —"), when this thread was entered via a card answer. */
  transition?: ReactNode;
  /** №22.3.1 (D-AF4) — a to-line above the composer ("to {actor} · steering this run"). */
  toLine?: ReactNode;
  /** №22.3.1 (D-AF2) — report streaming/composing up so the panel auto-pins. */
  onBusyChange?: (busy: boolean) => void;
  /**
   * №29 (S2) — where this thread's own chrome goes.
   *
   * `"section"` (default) is what S1 shipped and what the RAIL still needs: a
   * labelled section with its verbs on its own header row, because inside the
   * conversation there is no band above it to put them on.
   *
   * `"bare"` is the RUN SUBJECT. There, the stage head is that band — it already
   * names the run and its live state — so the section's eyebrow ("◎ Run · the
   * execution thread") would be a title under a title, and its buttons a second
   * toolbar under the first. The host renders `<RunThreadControls>` into the stage
   * head instead, so there is still exactly ONE Play button and one launch menu.
   * S1 named this as the per-subject toolbar grammar it was leaving to S2.
   */
  chrome?: "section" | "bare";
  /** What the panel puts above the fold on a CLICK (see ThreadLens). */
  lens?: ThreadLens;
}

/**
 * №29 (S2) — the run's VERBS, on their own so they can be rendered either on this
 * section's header row (in the rail) or portalled into the stage head (on the run
 * subject). One component, one Play mutation, one launch menu — whichever band it
 * lands on.
 */
export function RunThreadControls({ taskId, agentId, agentName, variant = "section" }: {
  taskId: string;
  agentId: string;
  agentName: string;
  /** `"stagehead"` uses the shell's own pill grammar (`.stagebtn`). */
  variant?: "section" | "stagehead";
}) {
  const thread = useTaskThread(taskId);
  const play = usePlayTaskRun();
  const [picker, setPicker] = useState(false);
  const launch = useTaskLaunchLinks(taskId, picker);
  const messages = thread.data?.conversation?.messages ?? [];
  const hasRun = messages.length > 0 || (thread.data?.runs?.length ?? 0) > 0;
  // №22.5 (F2) — any in-flight run for this task disables ▶, wherever it was
  // started, so two surfaces cannot fire the same run twice.
  const running = useIsRunPlaying(taskId);
  const head = variant === "stagehead";
  return (
    <>
      <div style={{ position: "relative" }}>
        <button
          type="button"
          {...(head ? { className: "stagebtn" } : { style: ghostBtn })}
          onClick={() => setPicker((v) => !v)}
          data-testid="launch-host"
        >
          <i className="ti ti-external-link" style={{ fontSize: 12 }} aria-hidden="true" /> {head ? "Launch in a host" : "Launch in Claude / ChatGPT"}
        </button>
        {picker && (
          <div style={vmenu} role="menu">
            <div style={vmenuHead}>Continue this task in a host — it returns here</div>
            {launch.isLoading && <div style={vmenuLoading}>Preparing links…</div>}
            {launch.data && (
              <>
                <a style={vmenuRow} href={launch.data.claude} target="_blank" rel="noopener noreferrer">
                  <span style={{ ...vmenuIc, background: "var(--kuvio-surface-claude-bg)", color: "var(--kuvio-surface-claude-fg)" }}>✦</span>
                  <span><span style={vmenuTt}>Launch in Claude</span><span style={vmenuDs}>Your subscription · returns to this thread</span></span>
                </a>
                <a style={vmenuRow} href={launch.data.chatgpt} target="_blank" rel="noopener noreferrer">
                  <span style={{ ...vmenuIc, background: "var(--kuvio-surface-chatgpt-bg)", color: "var(--kuvio-surface-chatgpt-fg)" }}>◎</span>
                  <span><span style={vmenuTt}>Launch in ChatGPT</span><span style={vmenuDs}>Connects via the Kuvio connector</span></span>
                </a>
              </>
            )}
          </div>
        )}
      </div>
      {agentId && (
        <button
          type="button"
          {...(head ? { className: "stagebtn stagebtn--primary" } : { style: playBtn })}
          disabled={running}
          onClick={() => play.mutate({ agentId, taskId, input: "", requestedByName: "you" })}
          data-testid="play-run"
        >
          <i className={running ? "ti ti-loader-2" : "ti ti-player-play"} style={{ fontSize: 13 }} aria-hidden="true" />
          {running ? "Running…" : hasRun ? "Run again" : `Play — run ${agentName}`}
        </button>
      )}
    </>
  );
}

// venue → chip { label, bg, fg } using the shipped surface tokens.
function venueChip(v: string | undefined): { label: string; bg: string; fg: string } {
  switch (v) {
    case "claude": return { label: "Claude", bg: "var(--kuvio-surface-claude-bg)", fg: "var(--kuvio-surface-claude-fg)" };
    case "chatgpt": return { label: "ChatGPT", bg: "var(--kuvio-surface-chatgpt-bg)", fg: "var(--kuvio-surface-chatgpt-fg)" };
    case "headless": return { label: "Headless", bg: "var(--kuvio-surface-code-bg)", fg: "var(--kuvio-surface-code-fg)" };
    default: return { label: "In-app", bg: "var(--color-background-info)", fg: "var(--color-text-info)" };
  }
}

function textOf(m: TaskThreadMessage): string {
  return (m.parts ?? []).filter((p) => p.type === "text").map((p) => p.text ?? "").join("\n").trim();
}

// ---------------------------------------------------------------------------
// №22.4 (D-HM1) — the ENDING, rendered. Three legal endings, one honest absence.
// ---------------------------------------------------------------------------

/** The chip for how a run ended. No ending recorded → that is what it says. */
function terminationChip(t: string | null): CSSProperties {
  const base: CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
    padding: "2px 9px", borderRadius: 999,
  };
  switch (t) {
    case "done_with_receipts":
      return { ...base, background: "var(--color-background-success, var(--color-background-secondary))", color: "var(--color-text-success)" };
    case "needs_you":
      return { ...base, background: "var(--color-background-warning, var(--color-background-secondary))", color: "var(--color-text-warning)" };
    case "blocked_honest":
      return { ...base, background: "var(--color-background-secondary)", color: "var(--color-text-secondary)" };
    default:
      return { ...base, background: "var(--color-background-secondary)", color: "var(--color-text-tertiary)" };
  }
}

function terminationFallback(t: string | null): string {
  switch (t) {
    case "done_with_receipts": return "✓ Done — with receipts";
    case "needs_you": return "◑ Needs you — raised a question and stopped";
    case "blocked_honest": return "⊘ Blocked — said what it lacks and stopped";
    default: return "· Ended without a recorded outcome — this step is still standing";
  }
}

function endingLabel(basis: string | null, n: number): string {
  if (basis === "attestation") return "✓ Done — attested (no record changed; the check itself is the outcome)";
  if (basis === "manifest") return `✓ Done — manifest satisfied${n ? ` · ${n} receipt${n === 1 ? "" : "s"}` : ""}`;
  return `✓ Done — ${n} receipt${n === 1 ? "" : "s"}`;
}

/** The verb effects behind a tick — the evidence, not a claim. */
function ReceiptChips({ receipts }: { receipts?: Array<{ verb: string; summary?: string | null; ref?: string | null; kind?: string }> }) {
  if (!receipts || receipts.length === 0) return null;
  return (
    <div style={receiptWrap} data-testid="run-receipts">
      {receipts.map((r, i) => (
        <span key={i} style={receiptChip} title={r.summary ?? r.verb}>
          <span style={verbChip}>{r.verb}</span>
          {r.ref && <span style={receiptRef}>{r.ref}</span>}
        </span>
      ))}
    </div>
  );
}

function toolNamesOf(m: TaskThreadMessage): string[] {
  const fromTrace = (m.metadata?.trace ?? []).flatMap((s) => (s.toolCalls ?? []).map((c) => c.name));
  const names = fromTrace.length ? fromTrace : (m.metadata?.tool_names ?? []);
  return names.filter(Boolean);
}

// ---------------------------------------------------------------------------
// №22.5 (Bundle 2) — THE RUN TRACE, made readable.
//
// The teardown's one genuine finding against us: you could read one of their finished
// runs cold and say what it read, how long it took and who worked; ours showed a column
// of mono verb names, which is a log. Everything below renders values DERIVED SERVER-
// SIDE (`metadata.trace_view`) from what the run actually recorded — there is no
// estimation on this side, and every absent fact renders as nothing rather than a zero.
//
// T5 is the shape of it: the collapsed receipt is the RESTING STATE. The line answers
// "what did it read, how long, who worked" without a click; the disclosure holds the
// enumeration for when the answer is disputed rather than read.
// ---------------------------------------------------------------------------

/**
 * №22.5 (F3) — the run STARTING, as its own line in the record.
 *
 * David, on the walk: *"there seems to be no record of me clicking the decision — it
 * would be nice to see the decision, or task RUN shown in the history."* The run's
 * beginning was invisible; the only trace of it was a composed sentence wearing his
 * name. This is that record, and it says three true things: a run started, who asked
 * for it, and — on request — exactly what the hire was handed.
 */
/**
 * №22.6.1 (D-NY6 §4, work item B) — `resumed` marks a run whose `run_start` message
 * carries `resumed_by` (derived server-side off `agent_runs.resumed_by`, item A's
 * stamp — see `tasks/thread` in workspace-do.ts). D-NY6 §4's own words are "the
 * thread shows '▶ resumed — you answered' as an event line" — the ▶ mark this line
 * already carries is exactly that event line; only the words next to it change.
 * The disclosure is unchanged: what a resumed run was actually handed (the answer,
 * via `answeringCard`) is exactly as fair a question as it is for a fresh run.
 */
function RunStartLine({ who, ask, resumed }: { who: string | null; ask: string; resumed?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={runStartRow} data-testid={resumed ? "run-resumed" : "run-start"}>
      <div style={runStartHead}>
        <span style={runStartMark}>▶</span>
        <span>{resumed ? "resumed — you answered" : `Run started${who ? ` by ${who}` : ""}`}</span>
        {ask && (
          <button type="button" style={discloseBtn} onClick={() => setOpen((v) => !v)} data-testid="run-start-disclose" aria-expanded={open}>
            {open ? "hide the brief" : "what was asked"}
          </button>
        )}
      </div>
      {open && ask && <div style={runStartAsk}>{ask}</div>}
    </div>
  );
}

/** T2 — one named source, a link when the server could build a route (never a 404). */
function SourceChip({ src, onNavigate }: { src: NamedSource; onNavigate: (href: string) => void }) {
  const body = (
    <>
      <span style={{ ...srcDot, background: src.kind === "search" ? "var(--c-purple-400)" : src.kind === "delegation" ? "var(--c-teal-600)" : "var(--color-text-tertiary)" }} />
      {src.label}
    </>
  );
  if (!src.href) return <span style={srcChip} title={src.verb} data-testid="trace-source">{body}</span>;
  return (
    <button type="button" style={{ ...srcChip, ...srcChipLink }} title={src.verb} onClick={() => onNavigate(src.href!)} data-testid="trace-source">
      {body}
    </button>
  );
}

function RunTrace({ view, steps, receipts }: {
  view: TraceView;
  steps: NonNullable<TaskThreadMessage["metadata"]>["trace"];
  receipts?: Array<{ verb: string; summary?: string | null; ref?: string | null; kind?: string }>;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const stepList = steps ?? [];
  // The disclosure exists only when there is something behind it — a detail block, a
  // per-step breakdown worth opening, or receipts to enumerate (R7-③: absent, never an
  // empty panel that costs a click to learn nothing).
  const hasDetail = !!view.detail || (receipts?.length ?? 0) > 0 || stepList.length > 1;

  return (
    <div style={traceWrap} data-testid="run-trace">
      {/* T4 — crew that worked IN PARALLEL, named, never hidden. Above the collapsed
          line because "three people worked on this" changes how the rest is read. */}
      {view.crew && <div style={crewLine} data-testid="trace-crew">{view.crew.text}</div>}

      {/* T5 — the resting state. */}
      <div style={collapsedRow}>
        {view.collapsed && <span style={collapsedText} data-testid="trace-collapsed">{view.collapsed}</span>}
        {hasDetail && (
          <button type="button" style={discloseBtn} onClick={() => setOpen((v) => !v)} data-testid="trace-disclose" aria-expanded={open}>
            {open ? "hide detail" : "detail"}
          </button>
        )}
      </div>

      {open && (
        <div style={traceBody}>
          {/* T1 + T2 — per step: how long it took, and what it touched, by name. */}
          {stepList.map((s, i) => {
            const sources = view.stepSources[i] ?? [];
            const elapsed = view.stepElapsed[i] ?? null;
            const names = (s.toolCalls ?? []).map((c) => c.name);
            if (sources.length === 0 && names.length === 0 && !elapsed) return null;
            return (
              <div key={i} style={stepRow} data-testid="trace-step">
                {/* Absent when this step was never timed — an untimed step shows no
                    time, rather than "0s", which would be a claim. */}
                {elapsed && <span style={stepTime} data-testid="trace-step-elapsed">{elapsed}</span>}
                <span style={stepBody}>
                  {sources.length > 0
                    ? sources.map((src, j) => <SourceChip key={j} src={src} onNavigate={navigate} />)
                    : names.map((n, j) => <span key={j} style={verbChip}>{n}</span>)}
                </span>
              </div>
            );
          })}

          {/* T3 — queries issued / sources read, enumerated. */}
          {view.detail && (
            <div style={detailBlock} data-testid="trace-detail">
              {view.detail.queries.length > 0 && (
                <div style={detailGroup}>
                  <div style={detailHead}>Queries issued</div>
                  {view.detail.queries.map((q, i) => <div key={i} style={detailLine}>{q}</div>)}
                </div>
              )}
              {view.detail.sources.length > 0 && (
                <div style={detailGroup}>
                  <div style={detailHead}>Read</div>
                  {view.detail.sources.map((s, i) => <div key={i} style={detailLine}>{s.label}</div>)}
                </div>
              )}
            </div>
          )}

          {/* The 22.4 receipts, enumerated here rather than at rest — the ENDING line
              above still states how many there are, so the claim never leaves the
              resting state, only its itemisation does. */}
          <ReceiptChips receipts={receipts} />
        </div>
      )}
    </div>
  );
}

export function RunThreadSection({ taskId, agentId, agentName, task, transition, toLine, onBusyChange, chrome = "section", lens = "full" }: Props) {
  const thread = useTaskThread(taskId);
  const play = usePlayTaskRun();
  const takeOver = useTakeOverTask();
  const answer = useAnswerNeedsYou();
  const skip = useSkipNeedsYou(); // №22.5 (C4) — skip records a consequence, never a vanish
  const coPanel = useCoPanelOptional(); // №22.3 (D-CH4) — continuation → panel, not a route
  // №29 (S2) — the launch-menu state moved into `RunThreadControls` with the buttons
  // it belongs to, so the menu is open in exactly one place regardless of which band
  // is carrying the verbs.
  const [draft, setDraft] = useState("");
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // №22.5 (F2) — "a run is in flight for THIS task", started here OR anywhere else.
  // It used to mean "this component pressed ▶", so a run started on the desk opened
  // an empty, silent thread for its whole duration — an agent acting with no surface
  // to watch it, which is the rule David restated on the walk.
  const running = useIsRunPlaying(taskId);

  /**
   * "read the full run →" opens the record in place — an ADDITIVE choice on top of
   * the lens, never a copy of it.
   *
   * It was `useState(lens === "full")`, seeded at mount, and that was wrong in the
   * one way this panel is built to expose: the docked panel is PORTALLED onto the
   * stage when the conversation is promoted (`conversation-host`) precisely so the
   * conversation survives — it does not remount. So a lens that changed from
   * `card-first` to `full` on promotion never reached the state, and the stage kept
   * the docked lens: the promoted conversation showed the ask where the record is
   * the subject, and the D-CH2 answer chain had nowhere to stream.
   *
   * Deriving it means the prop always wins and the reader's expansion is additive.
   * The panel keys this component by task id, so one ask's expansion does not leak
   * into the next one you click.
   */
  const [openedRecord, setOpenedRecord] = useState(false);
  /**
   * A RUN IN FLIGHT OPENS THE RECORD, AND KEEPS IT OPEN.
   *
   * D-NY5 scopes the panel to the ASK — right for a question waiting on you, and
   * WRONG the instant a run starts, because everything that shows a run happening
   * lives in the thread: the streaming dots, the transcript, the receipts. David
   * pressed "Run to complete" on the desk and the panel sat on "No run yet —
   * pressing ▶ starts one here" with no sign of life, then filed the finished run
   * under "Before this, on this task" behind a link. The run he had just started,
   * presented as history.
   *
   * `running` is `useIsRunPlaying` — 22.5 F2 made it mean "a run is in flight for
   * THIS task, started HERE OR ANYWHERE ELSE", which is exactly the signal needed:
   * the run began on the desk, not in this component.
   *
   * It is STICKY on purpose. Without that the record would snap shut the moment the
   * run ended and hide the result the reader was watching arrive — the same
   * disappearance D-CV1 exists to prevent, one layer up.
   */
  const [ranHere, setRanHere] = useState(false);
  const showRecord = lens === "full" || openedRecord || running || ranHere;

  // №22.3.1 (D-AF2) — a run streaming, or the composer holding text, auto-pins the
  // docked panel so navigation never yanks the conversation mid-thought.
  const busy = play.isPending || draft.trim().length > 0;
  useEffect(() => { onBusyChange?.(busy); return () => onBusyChange?.(false); }, [busy, onBusyChange]);
  // Latch the moment a run is seen in flight, so the record stays open after it ends.
  useEffect(() => { if (running) setRanHere(true); }, [running]);

  const messages = thread.data?.conversation?.messages ?? [];
  const hasRun = messages.length > 0 || (thread.data?.runs?.length ?? 0) > 0;
  // №22.4.2 (B) — there is no `brief` const here any more. It used to be
  // `task.instructions || task.body || task.title`, passed to ▶ as the run's ENTIRE
  // ask; the RUNNER now composes the task (title · anchor · brief · notes) into the
  // run itself, so ▶ sends no input and typing is an interjection on top of the task
  // rather than a replacement for it.
  const needsYou = task.needs_you_reason?.trim();
  const takenOver = !!task.taken_over_by_user_id;
  const a = task.anchor ?? null;
  const anchorLabel = a ? (a.name && a.name !== a.no ? `${a.name} · ${a.no}` : a.no) : undefined;
  const chosenIndex = answer.isPending ? (answer.variables?.optionIndex ?? null) : null;
  const answerErr = answer.isError ? ((answer.error as Error)?.message ?? "That option couldn’t be completed.") : null;
  const chooseOption = (optionIndex: number) => answer.mutate({
    taskId, optionIndex,
    // F8 — the label rides along so the optimistic line can NAME the choice.
    ...(task.needs_you_options?.[optionIndex]?.label ? { optionLabel: task.needs_you_options[optionIndex]!.label } : {}),
  });

  // №22.5 (F9) — open on the NEWEST message and follow the conversation, but only
  // while the reader is actually at the bottom (see the hook: scrolling up means
  // reading, and yanking someone off the evidence they went to check is worse than
  // making them scroll). The signal covers arrivals, the last id, and the working
  // indicator, which is itself a height change.
  /**
   * The runs that already happened on this task, each reduced to ONE line that says
   * what it did. Built from what the run already recorded (`termination_line` and its
   * receipts' summaries — D-HM1/T5), never from a re-reading of the transcript: the
   * run's own account of itself is the honest summary, and inventing a second one
   * would be a claim nobody made.
   */
  const priorRuns = messages
    .filter((m) => !!m.metadata?.run_id && (m.metadata?.run_termination || (m.metadata?.receipts?.length ?? 0) > 0))
    .map((m) => {
      const md = m.metadata!;
      const receipts = (md.receipts ?? []) as Array<{ summary?: string | null }>;
      const substance = receipts.map((r) => r.summary).filter((x): x is string => !!x && x.trim().length > 0);
      // Prefer what it CHANGED over how it ended: "land tax adjustment added" tells
      // you whether it bears on your question; "✓ Done — 1 receipt" does not.
      const line = substance.length ? substance.join(" · ") : (md.termination_line ?? "ran");
      const at = typeof md.at === "string" ? md.at : null;
      const t = at ? Date.parse(at) : NaN;
      const when = Number.isNaN(t) ? "" : new Date(t).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
      return { id: m.id, line, when };
    });

  /**
   * The card was answered, its verb ran, and nothing is in flight — the state that
   * read as "it's just sitting there". Derived, never stored: the last message is
   * an ANSWER, no question is standing, no run is playing, and the task is still
   * open. Any of those being false means something else is true and this line would
   * be a lie.
   */
  const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
  const answeredAndIdle =
    !!lastMsg?.metadata?.needs_you_answer
    && (lastMsg.metadata as { outcome?: string }).outcome !== "skipped"
    && !needsYou && !running && !takenOver
    && task.status !== "done" && task.status !== "dropped";

  const lastMessageId = messages.length > 0 ? messages[messages.length - 1]!.id : "";
  const endRef = useStickToBottom(`${messages.length}:${lastMessageId}:${running}`, !thread.isLoading);

  const runPlay = (input: string) => {
    if (!agentId) return;
    play.mutate({ agentId, taskId, input, requestedByName: "you" }, { onSuccess: () => setDraft("") });
  };

  const onSend = () => {
    const t = draft.trim();
    if (!t || running) return;
    runPlay(t);
  };

  return (
    <section style={{ marginBottom: 20 }} data-testid="run-thread-section">
      {/* №29 (S2) — on the RUN SUBJECT this whole band is absent: the stage head is
          the band, and it carries the same verbs (see `chrome`). */}
      {/* In CARD-FIRST the band is suppressed: it is titled "the execution thread"
          and names verbs for a thread this lens is not showing. The panel header
          already names the anchor and the hire; the verbs come back with the record. */}
      {chrome === "section" && showRecord && (
        <div style={headerRow}>
          <div style={{ ...eyebrow }}>◎ Run · the execution thread</div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <RunThreadControls taskId={taskId} agentId={agentId} agentName={agentName} />
          </div>
        </div>
      )}

      {/* PROPOSED — card-first. The thing you clicked, rendered as itself, at the
          top of the panel. The record is not hidden: the disclosure below opens the
          same thread this component renders in "full", and the count is real. */}
      {!showRecord && (
        <div style={threadWrap} data-testid="card-first-head">
          {transition}
          {needsYou ? (
            <div style={{ alignSelf: "stretch" }}>
              <DecisionCard
                variant="full"
                reason={needsYou}
                evidence={task.needs_you_evidence}
                options={task.needs_you_options}
                context={{ agentName, anchorLabel }}
                batch={task.needs_you_batch ?? null}
                provenance={task.provenance ?? null}
                skipConsequence={task.needs_you_skip_consequence ?? null}
                skipped={task.needs_you_skipped}
                onSkip={() => skip.mutate({ taskId })}
                skipping={skip.isPending}
                onReply={(text) => runPlay(text)}
                replying={running}
                onChoose={chooseOption}
                choosing={answer.isPending}
                chosenIndex={chosenIndex}
                onAssist={() => composerRef.current?.focus()}
                onTakeOver={() => takeOver.mutate({ taskId })}
                takingOver={takeOver.isPending}
                error={answerErr}
              />
            </div>
          ) : (
            /* No standing card — a QUEUED item. What you want here is not a chat
               log, it is what you asked for and the means to start it. */
            <div style={{ alignSelf: "stretch" }} data-testid="card-first-brief">
              <div style={{ fontWeight: 650, fontSize: 14, marginBottom: 4 }}>{task.title}</div>
              {anchorLabel && <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>{anchorLabel}</div>}
              <div style={{ fontSize: 12.5, color: "var(--text-2)", whiteSpace: "pre-wrap" }}>
                {task.instructions?.trim() || task.body?.trim() || `Nothing briefed yet — type below to brief ${agentName}.`}
              </div>
            </div>
          )}
          {/* WHAT CAME BEFORE — one self-describing line per run, not a count.
              A count ("3 earlier turns") is the trap: it asserts that something is
              there without saying what, so the reader has to open it to find out
              whether it bears on the question — which is the reconciliation work
              this lens exists to remove. Each line states what that run DID, from
              its own recorded termination line + receipts, so it can be dismissed
              at a glance without being read. Same discipline as №22.5's T5
              collapsed receipt: the resting state carries the substance. */}
          {priorRuns.length > 0 && (
            <div style={{ marginTop: 12 }} data-testid="prior-runs">
              <div style={{ fontSize: 11, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--text-3)", marginBottom: 6 }}>
                Before this, on this task
              </div>
              {priorRuns.map((r) => (
                <div key={r.id} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12, color: "var(--text-2)", padding: "3px 0" }}>
                  <span style={{ color: "var(--text-3)", minWidth: 44 }}>{r.when}</span>
                  <span>{r.line}</span>
                </div>
              ))}
              <button
                type="button"
                style={{ ...linkBtn, marginTop: 6 }}
                onClick={() => setOpenedRecord(true)}
                data-testid="read-full-run"
              >
                read the full run →
              </button>
            </div>
          )}
          {!hasRun && (
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-3)" }} data-testid="card-first-norun">
              No run yet — pressing ▶ starts one here.
            </div>
          )}
          {takenOver && <div style={takenOverLine}>✓ You took this task over — {agentName} has yielded.</div>}
        </div>
      )}

      {/* the thread */}
      {showRecord && (
      <div style={threadWrap}>
        {transition}
        {!hasRun && !running && (
          <div style={emptyThread}>
            No run yet. Press <b>Play</b> and {agentName} works the brief in this thread — you watch, and can
            interject by typing.
          </div>
        )}

        {messages.map((m) => {
          const text = textOf(m);
          if (m.role === "user") {
            // №22.5 (F3) — a RUN START composed by the runner is not something the human
            // said. It renders as what it is: the run beginning, attributed to whoever
            // pressed ▶, with the composed brief behind a disclosure — because "what was
            // my hire actually asked?" is a fair question and this is the only record of
            // the answer. A human who typed keeps their own bubble, below, unchanged.
            if (m.metadata?.run_start && m.metadata.composed) {
              return (
                <RunStartLine
                  key={m.id}
                  who={m.metadata.requested_by_name ?? null}
                  ask={text}
                  resumed={!!m.metadata.resumed_by}
                />
              );
            }
            return (
              <div key={m.id} style={{ ...bubble, ...userBubble, ...(m.metadata?.pending ? pendingTurn : null) }} data-pending={m.metadata?.pending ? "true" : undefined}>
                <div style={sndLabel}><span style={{ ...av, background: "var(--c-gray-600)" }}>DD</span>You</div>
                {text && <p style={msgText}>{text}</p>}
              </div>
            );
          }
          // assistant — escalation marker? render THE decision card (D-CC2/D-CC4, full).
          // №22.2 (D-CV1): once answered it renders RESOLVED IN PLACE — the permanent
          // record — instead of vanishing.
          if (m.metadata?.needs_you) {
            const r = m.metadata.resolved
              ? {
                  optionIndex: m.metadata.resolved_option_index ?? null,
                  label: m.metadata.resolved_option_label ?? null,
                  at: m.metadata.resolved_at ?? null,
                  byName: m.metadata.resolved_by ?? null,
                  outcome: m.metadata.resolved_outcome,
                  // №22.5 (C4) — a skip's receipt carries what it left standing.
                  consequence: m.metadata.resolved_consequence ?? null,
                }
              : null;
            return (
              <div key={m.id} style={{ alignSelf: "stretch", marginLeft: 26 }}>
                <DecisionCard
                  variant="full"
                  reason={text}
                  evidence={m.metadata.needs_you_evidence ?? task.needs_you_evidence}
                  options={m.metadata.needs_you_options ?? task.needs_you_options}
                  context={{ anchorLabel }}
                  resolved={r}
                  // №22.5 (C1) — the batch position rides the MESSAGE, not the task, so
                  // an older card further up the thread keeps the pager it was raised
                  // with instead of re-labelling itself with the live cursor.
                  batch={m.metadata.needs_you_batch ?? null}
                  provenance={task.provenance ?? null}
                  skipConsequence={m.metadata.needs_you_skip_consequence ?? task.needs_you_skip_consequence ?? null}
                  skipped={r ? undefined : task.needs_you_skipped}
                  onSkip={r ? undefined : () => skip.mutate({ taskId })}
                  skipping={skip.isPending}
                  // F10 — the same call the composer makes. On the thread both exist,
                  // and that is fine: the box is beside the question you are answering,
                  // the composer is for everything else.
                  onReply={r ? undefined : (text) => runPlay(text)}
                  replying={running}
                  onChoose={r ? undefined : chooseOption}
                  choosing={answer.isPending}
                  chosenIndex={chosenIndex}
                  onAssist={r ? undefined : () => composerRef.current?.focus()}
                  onTakeOver={r ? undefined : () => takeOver.mutate({ taskId })}
                  takingOver={takeOver.isPending}
                  error={r ? null : answerErr}
                />
              </div>
            );
          }
          // assistant — the answer CONSEQUENCE CHAIN (D-CV2): choice → action →
          // consequence → continuation, each on its own line (falls back to the
          // single choice text for pre-22.2 messages).
          if (m.metadata?.needs_you_answer) {
            const failed = m.metadata.outcome === "failed";
            const events = m.metadata.answer_events ?? [{ kind: "choice" as const, text }];
            return (
              <div key={m.id} style={{ ...answerChain, ...(m.metadata?.pending ? pendingTurn : null) }} data-testid="answer-chain" data-pending={m.metadata?.pending ? "true" : undefined}>
                {events.map((e, i) => {
                  if (e.kind === "action") {
                    return <div key={i} style={chainAction}><span style={verbChip}>{e.tool ?? e.text}</span></div>;
                  }
                  if (e.kind === "continuation" && e.href) {
                    // D-CH4 — focus the panel on the next run's thread; no full reload.
                    const nextId = taskIdFromThreadHref(e.href);
                    return (
                      <button
                        key={i}
                        type="button"
                        style={{ ...chainContinue, border: 0, background: "none", padding: 0, cursor: "pointer", font: "inherit", textAlign: "left" }}
                        onClick={() => { if (coPanel && nextId) coPanel.focusTask({ taskId: nextId }); }}
                        data-testid="chain-continue"
                      >
                        {e.text}
                      </button>
                    );
                  }
                  const color = e.kind === "choice"
                    ? (failed ? "var(--color-text-danger)" : "var(--color-text-success)")
                    : "var(--color-text-secondary)";
                  return <div key={i} style={{ ...chainLine, color, fontWeight: e.kind === "choice" ? 600 : 400 }}>{e.text}</div>;
                })}
              </div>
            );
          }
          // №22.4 (work item A) — the honest degradation rung. A run that did NOT
          // happen SAYS so; the task stays standing and nothing is ticked.
          if (m.metadata?.degraded) {
            return (
              <div key={m.id} style={degradedLine} data-testid="degradation-line">
                <i className="ti ti-alert-triangle" style={{ fontSize: 13, marginRight: 6 }} aria-hidden="true" />
                {text}
              </div>
            );
          }
          // №22.4 (D-HM1) — a standalone ENDING record: written at the write-side gate
          // when the tick arrived from outside a native run (a frontier host, an export
          // token). Shows the basis and the attestation, verbatim.
          if (m.metadata?.run_termination && !m.metadata.run_id) {
            return (
              <div key={m.id} style={endingBlock} data-testid="run-ending">
                <div style={endingHead}>{endingLabel(m.metadata.termination_basis ?? null, m.metadata.receipts?.length ?? 0)}</div>
                {m.metadata.attestation && <div style={attestLine}>“{m.metadata.attestation}”</div>}
                <ReceiptChips receipts={m.metadata.receipts} />
              </div>
            );
          }
          const tools = toolNamesOf(m);
          const md = m.metadata;
          // №22.5 (T5) — when the run recorded a trace, the COLLAPSED receipt replaces
          // the flat chip column as the resting state. Without one (older runs, a
          // provider leg that reported no steps) the chips stay exactly as they were:
          // the legibility upgrade never removes information it cannot replace.
          const traceView = md?.trace_view ?? null;
          return (
            <div key={m.id} style={{ ...bubble, ...agentBubble }}>
              <div style={sndLabel}><span style={{ ...av, background: "var(--c-teal-600)" }}>{agentName.slice(0, 2).toUpperCase()}</span>{agentName}</div>
              {text && <div style={mdWrap}><ChatMarkdown text={text} /></div>}
              {traceView
                ? <RunTrace view={traceView} steps={md?.trace} receipts={md?.receipts} />
                : tools.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                    {tools.map((t, i) => (
                      <span key={i} style={actLine}><span style={verbChip}>{t}</span></span>
                    ))}
                  </div>
                )}
              {/* №22.4 (D-HM1) — how the run ENDED, on the run itself. A run that
                  reached none of the three legal endings says exactly that: the
                  absence is the information, never a silent pass. */}
              {md?.run_id && (
                <div style={endingRow} data-testid="run-termination">
                  <span style={terminationChip(md.run_termination ?? null)}>
                    {md.termination_line ?? terminationFallback(md.run_termination ?? null)}
                  </span>
                  {(md.refusals ?? 0) > 0 && (
                    <span style={refusalChip} title="the receipts gate refused a bare tick">
                      {md.refusals} tick{md.refusals === 1 ? "" : "s"} refused
                    </span>
                  )}
                </div>
              )}
              {/* №22.5 (F12) — what the run TRIED and could not do. Beside the ending,
                  because it is part of how the run went: four failed writes used to be
                  invisible, so a run that fought and lost looked like one that idled. */}
              {(md?.failed_effects?.length ?? 0) > 0 && (
                <div style={failedWrap} data-testid="failed-effects">
                  {md!.failed_effects!.map((f, i) => (
                    <div key={i} style={failedLine}>
                      <span style={failedMark}>✗</span>
                      <span><b>{f.verb}</b>{f.summary ? ` — ${f.summary}` : " — refused"}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* №22.5 (T5) — receipts are enumerated INSIDE the trace disclosure when
                  there is one; the ending line above always states how many there are,
                  so the claim stays at rest and only its itemisation moves. Runs with
                  no trace keep them here, unchanged. */}
              {md?.run_id && !traceView && <ReceiptChips receipts={md.receipts} />}
              {md && (md.tokens_in != null || md.venue) && (
                <div style={completion}>
                  {md.venue && (() => { const c = venueChip(md.venue); return <span style={{ ...venuePill, background: c.bg, color: c.fg }}><span style={venueDot} />{c.label}</span>; })()}
                  {" "}
                  {md.model ? md.model.replace(/^claude-/, "").replace(/-\d{8}$/, "") : ""}
                  {md.tokens_in != null ? ` · ${((md.tokens_in ?? 0) + (md.tokens_out ?? 0)).toLocaleString()} tokens` : ""}
                  {md.tool_calls ? ` · ${md.tool_calls} tool calls` : ""}
                </div>
              )}
            </div>
          );
        })}

        {running && (
          <div style={{ ...bubble, ...agentBubble }}>
            <div style={sndLabel}><span style={{ ...av, background: "var(--c-teal-600)" }}>{agentName.slice(0, 2).toUpperCase()}</span>{agentName}</div>
            <div style={streamDots} data-testid="stream-dots"><span className="streamdot" style={dot} /><span className="streamdot" style={dot} /><span className="streamdot" style={dot} /></div>
          </div>
        )}

        {/* standing escalation from the task record (in case the thread message isn't loaded) */}
        {needsYou && !messages.some((m) => m.metadata?.needs_you) && (
          <div style={{ alignSelf: "stretch", marginLeft: 26 }}>
            <DecisionCard
              variant="full"
              reason={needsYou}
              evidence={task.needs_you_evidence}
              options={task.needs_you_options}
              context={{ anchorLabel }}
              batch={task.needs_you_batch ?? null}
              provenance={task.provenance ?? null}
              skipConsequence={task.needs_you_skip_consequence ?? null}
              skipped={task.needs_you_skipped}
              onSkip={() => skip.mutate({ taskId })}
              skipping={skip.isPending}
              onReply={(text) => runPlay(text)}
              replying={running}
              onChoose={chooseOption}
              choosing={answer.isPending}
              chosenIndex={chosenIndex}
              onAssist={() => composerRef.current?.focus()}
              onTakeOver={() => takeOver.mutate({ taskId })}
              takingOver={takeOver.isPending}
              error={answerErr}
            />
          </div>
        )}

        {takenOver && <div style={takenOverLine}>✓ You took this task over — {agentName} has yielded.</div>}

        {/* №22.6 (F29) — WHAT HAPPENS NEXT, said out loud.
            David, after answering a card: "it also shows nothing more. Wtf is it
            doing? Is it done? Is it a situation where it can't do anything?"

            What actually happened, verified live: the chosen option's prepared call
            RAN (`executed_tool` comes back on the answer), and then the hire stopped
            — because raising is a TERMINAL run ending (22.4 D-HM1) and answering
            executes the verb without starting a new run. Correct by the rules, and
            completely silent, which is the F12 shape all over again: every layer
            individually right, nothing joining them.

            So the thread says it, and offers the way on. Continuing is a HUMAN press,
            never automatic: a run costs real money and the autonomy dial is the
            contract for spending it. */}
        {answeredAndIdle && (
          <div style={pausedRow} data-testid="run-paused">
            <span>◑ Your answer ran. <b>{agentName}</b> is paused — it does not continue on its own.</span>
            <button
              type="button"
              style={continueBtn}
              data-testid="run-continue"
              disabled={!agentId || running}
              onClick={() => runPlay("")}
            >
              ▶ Continue the run
            </button>
          </div>
        )}

        {/* №22.5 (F9) — the bottom of the conversation. The thread opens HERE, not at
            its oldest turn, and follows new content while you are reading the end. */}
        <div ref={endRef} aria-hidden="true" data-testid="thread-end" />
      </div>
      )}

      {/* composer — interject / assist */}
      {agentId && toLine}
      {agentId && (
        <div style={composerRoot}>
          <textarea
            ref={composerRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
            // D-L6 — the hire's NAME, which this composer already has two branches
            // down. "the agent continues" was the one place it said the word instead.
            placeholder={needsYou ? `Reply to assist — ${agentName} continues…` : hasRun ? "Reply, or steer the run…" : `Type to brief ${agentName}, or press Play…`}
            rows={1}
            style={composerInput}
            data-testid="thread-composer"
          />
          <button type="button" style={sendBtn} disabled={!draft.trim() || running} onClick={onSend} title="Send">↑</button>
        </div>
      )}
      {play.isError && <div style={errLine}>{(play.error as Error)?.message ?? "The run failed."}</div>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// styles (token-only, theme-aware)
// ---------------------------------------------------------------------------
const headerRow: CSSProperties = { display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" };
const eyebrow: CSSProperties = { fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", color: "var(--color-text-secondary)", textTransform: "uppercase" };
const playBtn: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 9, border: "none", background: "var(--color-text-primary)", color: "var(--color-background-primary)", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const ghostBtn: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 9, border: "0.5px solid var(--color-border-secondary)", background: "transparent", color: "var(--color-text-secondary)", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const threadWrap: CSSProperties = { display: "flex", flexDirection: "column", gap: 10, padding: "4px 0" };
const emptyThread: CSSProperties = { fontSize: 12.5, lineHeight: 1.6, color: "var(--color-text-secondary)", padding: "14px 16px", borderRadius: 12, background: "var(--color-background-secondary)", border: "0.5px dashed var(--color-border-secondary)" };
const bubble: CSSProperties = { maxWidth: "92%", padding: "10px 13px", borderRadius: 14, border: "0.5px solid var(--color-border-tertiary)" };
const userBubble: CSSProperties = { alignSelf: "flex-end", background: "var(--color-background-info)" };
const agentBubble: CSSProperties = { alignSelf: "flex-start", background: "var(--color-background-secondary)" };
const sndLabel: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 5, display: "flex", alignItems: "center", gap: 6 };
const av: CSSProperties = { width: 18, height: 18, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff" };
const msgText: CSSProperties = { margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--color-text-primary)", whiteSpace: "pre-wrap" };
// №22.3.1 (P2) — the agent bubble renders markdown; ChatMarkdown owns block spacing,
// so the wrapper just trims the leading/trailing paragraph margins inside the bubble.
const mdWrap: CSSProperties = { margin: "-8px 0" };
const actLine: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 8px", borderRadius: 9, background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)" };
const verbChip: CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10.5, padding: "2px 6px", borderRadius: 6, background: "var(--color-background-tertiary)", color: "var(--color-text-secondary)" };
const completion: CSSProperties = { marginTop: 8, paddingTop: 7, borderTop: "0.5px solid var(--color-border-tertiary)", fontSize: 11, color: "var(--color-text-tertiary)", fontVariantNumeric: "tabular-nums", display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" };
const venuePill: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 999 };
const venueDot: CSSProperties = { width: 5, height: 5, borderRadius: "50%", background: "currentColor" };
const pausedRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
  margin: "6px 0 2px 26px", fontSize: 12.5, color: "var(--text-2)",
};
const continueBtn: CSSProperties = {
  border: "1px solid var(--accent)", background: "var(--accent-wash)", color: "var(--accent)",
  borderRadius: 8, padding: "4px 10px", fontSize: 12.5, fontWeight: 650, cursor: "pointer",
};
const streamDots: CSSProperties = { display: "inline-flex", gap: 4, padding: "3px 0" };
const dot: CSSProperties = { width: 6, height: 6, borderRadius: "50%", background: "var(--c-purple-400)", opacity: 0.5 };
// №22.2 (D-CV2) — the answer consequence chain, drawn line-by-line.
const answerChain: CSSProperties = { alignSelf: "flex-start", display: "flex", flexDirection: "column", gap: 3, padding: "2px 4px 2px 26px" };
const chainLine: CSSProperties = { fontSize: 12.5, lineHeight: 1.45 };
const chainAction: CSSProperties = { display: "flex" };
const chainContinue: CSSProperties = { fontSize: 12.5, fontWeight: 600, color: "var(--color-text-link, var(--c-teal-600))", textDecoration: "none" };
const takenOverLine: CSSProperties = { fontSize: 12, color: "var(--color-text-secondary)", padding: "8px 12px", borderRadius: 10, background: "var(--color-background-secondary)" };
// №22.4 (D-HM1) — the ending + its receipts; and the honest degradation rung.
const endingRow: CSSProperties = { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 8 };
const refusalChip: CSSProperties = { fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "var(--color-background-tertiary)", color: "var(--color-text-tertiary)" };
// №22.5 (F12) — what the run tried and could not do.
const failedWrap: CSSProperties = { display: "flex", flexDirection: "column", gap: 3, marginTop: 7 };
const failedLine: CSSProperties = { display: "flex", gap: 7, alignItems: "baseline", fontSize: 11.5, lineHeight: 1.45, color: "var(--color-text-danger)" };
const failedMark: CSSProperties = { flex: "0 0 auto", fontWeight: 700 };
/**
 * №22.5 (F8) — an optimistic turn, visibly provisional. Dimmed, not hidden: the point
 * is that your action is ON SCREEN immediately, while still reading as in-flight rather
 * than as a recorded fact. It is replaced by the server's own line moments later.
 */
const pendingTurn: CSSProperties = { opacity: 0.55 };
// ── №22.5 (F3) — the run-start line. A hairline record, not a bubble. ────────
/** A link-shaped button. `<a>` would be a route affordance, which D-CH3 bans in
 *  the loop: this opens the record IN PLACE, it does not navigate. */
const linkBtn: CSSProperties = {
  border: "none", background: "none", padding: 0, cursor: "pointer",
  font: "inherit", fontSize: 12, fontWeight: 650, color: "var(--accent)",
};

const runStartRow: CSSProperties = { alignSelf: "stretch", padding: "2px 0 2px 26px", display: "flex", flexDirection: "column", gap: 4 };
const runStartHead: CSSProperties = {
  display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap",
  fontSize: 11.5, fontWeight: 600, color: "var(--color-text-secondary)",
};
const runStartMark: CSSProperties = { color: "var(--color-text-tertiary)", fontSize: 10 };
const runStartAsk: CSSProperties = {
  fontSize: 12, lineHeight: 1.5, color: "var(--color-text-secondary)", whiteSpace: "pre-wrap",
  padding: "8px 11px", borderRadius: 9, background: "var(--color-background-secondary)",
  border: "0.5px dashed var(--color-border-secondary)",
};
// ── №22.5 (Bundle 2) — the run trace. Token-only, theme-aware. ───────────────
const traceWrap: CSSProperties = { marginTop: 8, display: "flex", flexDirection: "column", gap: 5 };
const crewLine: CSSProperties = {
  fontSize: 11.5, fontWeight: 600, color: "var(--color-text-secondary)",
  display: "flex", alignItems: "center", gap: 6, lineHeight: 1.4,
};
const collapsedRow: CSSProperties = { display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" };
const collapsedText: CSSProperties = { fontSize: 11.5, color: "var(--color-text-tertiary)", fontVariantNumeric: "tabular-nums" };
const discloseBtn: CSSProperties = {
  border: "none", background: "none", padding: 0, cursor: "pointer",
  fontSize: 11.5, fontWeight: 600, color: "var(--color-text-link, var(--c-teal-600))",
};
const traceBody: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 6, marginTop: 2, paddingTop: 7,
  borderTop: "0.5px solid var(--color-border-tertiary)",
};
const stepRow: CSSProperties = { display: "flex", gap: 8, alignItems: "flex-start" };
const stepTime: CSSProperties = {
  flex: "0 0 auto", minWidth: 40, textAlign: "right", fontSize: 10.5,
  color: "var(--color-text-tertiary)", fontVariantNumeric: "tabular-nums", paddingTop: 3,
};
const stepBody: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 5, minWidth: 0 };
const srcChip: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px", borderRadius: 8,
  background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)",
  fontSize: 11.5, color: "var(--color-text-secondary)", textAlign: "left",
};
const srcChipLink: CSSProperties = { cursor: "pointer", color: "var(--color-text-link, var(--c-teal-600))", fontWeight: 600 };
const srcDot: CSSProperties = { width: 5, height: 5, borderRadius: "50%", flex: "0 0 auto" };
const detailBlock: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 8, padding: "8px 11px", borderRadius: 9,
  background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)",
};
const detailGroup: CSSProperties = { display: "flex", flexDirection: "column", gap: 2 };
const detailHead: CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
  color: "var(--color-text-tertiary)",
};
const detailLine: CSSProperties = { fontSize: 11.5, color: "var(--color-text-secondary)", lineHeight: 1.45 };
const receiptWrap: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 };
const receiptChip: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 7px", borderRadius: 8, background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)" };
const receiptRef: CSSProperties = { fontSize: 10.5, color: "var(--color-text-tertiary)", fontVariantNumeric: "tabular-nums" };
const endingBlock: CSSProperties = { alignSelf: "flex-start", marginLeft: 26, padding: "8px 12px", borderRadius: 10, background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)" };
const endingHead: CSSProperties = { fontSize: 12, fontWeight: 600, color: "var(--color-text-success)" };
const attestLine: CSSProperties = { marginTop: 4, fontSize: 12.5, lineHeight: 1.5, color: "var(--color-text-secondary)", fontStyle: "italic" };
const degradedLine: CSSProperties = { alignSelf: "stretch", display: "flex", alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.5, color: "var(--color-text-warning, var(--color-text-secondary))", padding: "9px 13px", borderRadius: 10, background: "var(--color-background-secondary)", border: "0.5px dashed var(--color-border-secondary)" };
const composerRoot: CSSProperties = { marginTop: 12, display: "flex", alignItems: "flex-end", gap: 8, padding: 7, borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-secondary)" };
const composerInput: CSSProperties = { flex: 1, resize: "none", background: "transparent", border: "none", outline: "none", fontSize: 13, lineHeight: 1.5, color: "var(--color-text-primary)", fontFamily: "var(--font-sans)", padding: "6px 8px", maxHeight: 120, minHeight: 20 };
const sendBtn: CSSProperties = { width: 32, height: 32, borderRadius: 8, border: "none", background: "var(--color-text-primary)", color: "var(--color-background-primary)", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flex: "0 0 auto" };
const errLine: CSSProperties = { marginTop: 8, fontSize: 12, color: "var(--color-text-danger)" };
const vmenu: CSSProperties = { position: "absolute", right: 0, top: "calc(100% + 6px)", width: 300, zIndex: 30, borderRadius: 12, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-primary)", overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" };
const vmenuHead: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", padding: "10px 14px 6px" };
const vmenuLoading: CSSProperties = { fontSize: 12, color: "var(--color-text-tertiary)", padding: "6px 14px 12px" };
const vmenuRow: CSSProperties = { display: "flex", alignItems: "center", gap: 11, padding: "11px 14px", borderTop: "0.5px solid var(--color-border-tertiary)", textDecoration: "none", cursor: "pointer" };
const vmenuIc: CSSProperties = { width: 30, height: 30, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, flex: "0 0 auto" };
const vmenuTt: CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" };
const vmenuDs: CSSProperties = { display: "block", fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 1 };
